import React, { FC, useState } from 'react';
import { IconButton } from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import './DocumentUploadField.css';

export interface DocumentUploadFieldProps {
	label: string;
	mandatory?: boolean;
	helperText?: string;
	onFilesChange: (files: FileList) => void;
	uploadedFileNames?: string[];
	onRemoveFile?: (index: number) => void;
	wrong?: boolean;
	wrongMessage?: string;
	verticalLayout?: boolean;
	accept?: string;
	onInfoClick?: () => void;
	onInfoLabel?: string;
	emptyLabel?: string;
	dropPrefix?: string;
	browseLabel?: string;
}

const DocumentUploadField: FC<DocumentUploadFieldProps> = ({
	label,
	mandatory = false,
	helperText,
	onFilesChange,
	uploadedFileNames = [],
	onRemoveFile,
	wrong,
	wrongMessage,
	verticalLayout = false,
	accept,
	onInfoClick,
	onInfoLabel,
	emptyLabel,
	dropPrefix,
	browseLabel,
}) => {
	const [isDragging, setIsDragging] = useState(false);
	const hasUploadedFiles = uploadedFileNames.length > 0;

	const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setIsDragging(true);
	};

	const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		const nextTarget = event.relatedTarget;
		if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) return;
		setIsDragging(false);
	};

	const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setIsDragging(false);
		if (event.dataTransfer.files.length > 0) {
			onFilesChange(event.dataTransfer.files);
		}
	};

	return (
		<div className="document-upload-field">
			<div
				className={`document-upload-field__card${isDragging ? ' is-dragging' : ''}`}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
			>
				<div className="document-upload-field__details">
					<div className="document-upload-field__heading">
						<div className="document-upload-field__title">
							{label} {mandatory && <span aria-hidden="true">*</span>}
						</div>
						{onInfoClick && (
							<IconButton
								type="button"
								aria-label={onInfoLabel ?? 'Info'}
								title={onInfoLabel}
								size="small"
								onClick={(event) => {
									event.stopPropagation();
									onInfoClick();
								}}
								className="document-upload-field__info"
							>
								<LightbulbOutlinedIcon fontSize="small" />
							</IconButton>
						)}
					</div>
					{helperText && <div className="document-upload-field__helper">{helperText}</div>}
				</div>

				<label
					className="document-upload-field__drop-action"
					aria-label={hasUploadedFiles ? `${dropPrefix}${browseLabel}` : `${emptyLabel}. ${dropPrefix}${browseLabel}`}
				>
					<input
						type="file"
						multiple
						accept={accept}
						onChange={(event) => {
							if (event.currentTarget.files && event.currentTarget.files.length > 0) {
								onFilesChange(event.currentTarget.files);
							}
						}}
					/>
					<img src="/images/upload.png" alt="" className="document-upload-field__upload-icon" />
					<span className="document-upload-field__drop-copy">
						{!hasUploadedFiles && <span className="document-upload-field__empty">{emptyLabel}</span>}
						<span className="document-upload-field__prompt">
							{dropPrefix}<span className="document-upload-field__browse">{browseLabel}</span>
						</span>
					</span>
				</label>

				{uploadedFileNames.length > 0 && (
					<div className={`document-upload-field__files${verticalLayout ? ' is-vertical' : ''}`}>
						{uploadedFileNames.map((fileName, index) => (
							<div key={`${fileName}-${index}`} className="document-upload-field__file">
								<span>{fileName}</span>
								{onRemoveFile && (
									<button
										type="button"
										aria-label={`Supprimer ${fileName}`}
										title={`Supprimer ${fileName}`}
										onClick={() => onRemoveFile(index)}
									>
										×
									</button>
								)}
							</div>
						))}
					</div>
				)}
			</div>
			{wrong && <div className="document-upload-field__error" role="alert">{wrongMessage}</div>}
		</div>
	);
};

export default DocumentUploadField;
